import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { NumField } from "./Custom";
import { RollStep, ShapeCard, SizeTile, StockCard, CornerTile } from "../components/RollParts";
import { RollSummary } from "../components/RollSummary";
import { ROLL_SIZES, ROLL_CORNERS, hasCorners, rollStickerPrice } from "../data/rollStickerPricing";
import { Ban } from "lucide-react";

const SHAPES = ["Square", "Round", "Rectangle", "Oval", "Custom"];
const SHAPE_GROUP = { Square: "square", Round: "square", Rectangle: "rect", Oval: "rect" };
const sizesFor = (shape) => ROLL_SIZES.filter((s) => !SHAPE_GROUP[shape] || s.group === SHAPE_GROUP[shape]);
const STOCKS = [
  { name: "White BOPP", image: "/products/stock-white-bopp.webp", desc: "White BOPP is a durable, water-resistant synthetic material with a glossy finish." },
  { name: "Paper", image: "/products/stock-paper.webp", desc: "Paper Labels are a versatile indoor option with a smooth matt paper finish and crisp, gloss UV-printed colour." },
];

export default function RollBuilder() {
  const [shape, setShape] = useState("Square");
  const [sizeId, setSizeId] = useState("40x40");
  const [stock, setStock] = useState(STOCKS[0].name);
  const [corner, setCorner] = useState(ROLL_CORNERS[0]);
  const [qty, setQty] = useState("1000");
  const [file, setFile] = useState(null);
  const [copyright, setCopyright] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const sizes = sizesFor(shape);
  const size = ROLL_SIZES.find((s) => s.id === sizeId);
  const q = parseInt(qty, 10) || 0;
  const result = useMemo(() => rollStickerPrice({ sizeId, qty: q }), [sizeId, q]);
  const corners = hasCorners(shape);

  const chooseShape = (s) => {
    setShape(s);
    const list = sizesFor(s);
    if (!list.some((x) => x.id === sizeId)) setSizeId(list[0].id);
  };

  const addToCart = () => {
    if (result.status !== "ok") { toast.error(result.message); return; }
    if (!file) { toast.error("Please upload your artwork first"); return; }
    if (!copyright) { toast.error("Please confirm you own or have permission to print this artwork"); return; }
    addItem({
      product_type: "custom",
      product_name: "Stickers on Rolls",
      shape: `${shape} · ${stock}${corners ? ` · ${corner} mm corners` : ""}`,
      size: size.label,
      width_mm: size.w,
      height_mm: size.h,
      quantity: q,
      unit_price: result.price / q,
      line_total: result.price,
      needs_quote: false,
      artwork_file_id: file.id,
      artwork_filename: file.name,
      artwork_files: [{ file_id: file.id, filename: file.name, design: "Design 1" }],
      artwork_mode: "single",
      copyright_confirmed: true,
    });
    toast.success("Stickers on rolls added to cart");
    navigate("/cart");
  };

  return (
    <div className="App bg-[#FCFBF9]">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 pb-4">
        <div className="eyebrow" data-testid="custom-mode-eyebrow">Stickers on rolls</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight" data-testid="roll-title">Stickers on Rolls</h1>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl">Custom stickers on rolls, printed in vibrant colour. Perfect for product labels, packaging, branding and more.</p>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-6 space-y-12">
        <div className="space-y-12">
          <RollStep n={1} title="Choose your shape" hint="The available sizes update based on your shape." testid="roll-step-shape">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="roll-shape-options">
              {SHAPES.map((s) => <ShapeCard key={s} shape={s} active={shape === s} onClick={() => chooseShape(s)} />)}
            </div>
          </RollStep>

          <RollStep n={2} title="Choose your size" hint={`Select a size for your ${shape.toLowerCase()} sticker.`} testid="roll-step-size">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="roll-size-options">
              {sizes.map((s) => <SizeTile key={s.id} size={s} shape={shape} active={sizeId === s.id} onClick={() => setSizeId(s.id)} />)}
            </div>
          </RollStep>

          <RollStep n={3} title="Choose your stock" hint="Both materials are high quality and printed in vibrant colour." testid="roll-step-stock">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="roll-stock-options">
              {STOCKS.map((s) => <StockCard key={s.name} stock={s} active={stock === s.name} onClick={() => setStock(s.name)} />)}
            </div>
          </RollStep>

          <RollStep n={4} title="Choose your corner radius" hint="For square and rectangle stickers." testid="roll-step-corner">
            {corners ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="roll-corner-options">
                {ROLL_CORNERS.map((r) => <CornerTile key={r} r={r} active={corner === r} onClick={() => setCorner(r)} />)}
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-[#ECE7E0] bg-white p-5 flex items-center gap-3 text-sm text-slate-600" data-testid="roll-corner-na">
                <Ban size={18} className="text-slate-400 flex-shrink-0" />
                Corner radius doesn't apply to {shape.toLowerCase()} stickers — they're cut to the {shape === "Custom" ? "outline of your artwork" : "shape's curve"}.
              </div>
            )}
          </RollStep>

          <RollStep n={5} title="Enter your quantity" hint="Order any quantity. The price updates automatically." testid="roll-step-qty">
            <div className="max-w-sm">
              <NumField label="Number of stickers" value={qty} onChange={setQty} testid="roll-qty" placeholder="e.g. 1000" />
            </div>
          </RollStep>
        </div>

        <RollStep n={6} title="Your price & artwork" hint="Check your selection, upload your artwork and add to cart." testid="roll-step-price">
          <RollSummary result={result} q={q} onAdd={addToCart} file={file} setFile={setFile} copyright={copyright} setCopyright={setCopyright}
            rows={[
              ["Shape", shape, "roll-summary-shape"],
              ["Size", size.label, "roll-summary-size"],
              ["Stock", stock, "roll-summary-stock"],
              ...(corners ? [["Corner radius", `${corner} mm`, "roll-summary-corner"]] : []),
              ["Quantity", q.toLocaleString(), "roll-summary-qty"],
            ]} />
        </RollStep>
      </section>
      <Footer />
    </div>
  );
}
